// La extracción de texto pdfjs se mudó al util compartido src/utils/pdf/extraerTextoPdf.js (mismo
// setup de worker + mismo join) para que los parsers por proveedor la reusen. Se RE-EXPORTA acá
// para no romper imports existentes de `extraerTextoPdf` desde este archivo — OnSign no cambia de
// comportamiento.
import { extraerTextoPdf } from "./pdf/extraerTextoPdf";

// Re-export para no romper imports históricos de `extraerTextoPdf` desde este archivo.
export { extraerTextoPdf } from "./pdf/extraerTextoPdf";

/**
 * Parser de la factura PDF de OnSign (proveedor extranjero de señalización, sin DTE SII) → los 5
 * datos que pre-llenan la carga de factura externa. 100% en cliente, sin subir el archivo.
 *
 * El texto de OnSign sale legible con pdfjs (fuentes con Unicode limpio — validado en PoC), así que
 * NO hace falta el doble camino DOMParser+regex de parseSwiftPdf: alcanza con regex sobre el texto
 * completo. CRÍTICO: parsear por PATRÓN, no por posición — la cantidad de líneas varía (una factura
 * puede traer líneas extra de prorrateo / "Subscription Change +1"). Ver anclaje de licencias abajo.
 *
 * Devuelve el SHAPE TRANSVERSAL { folio, fecha, periodo, concepto, monto, moneda, unidades } (idéntico
 * al de los 6 parsers nuevos de src/utils/pdf/parsers/) o lanza Error si no reconoce el formato (folio
 * y monto obligatorios). `concepto` sale null: OnSign no trae nombre de producto, lo arma el hook.
 * OJO `periodo`: OnSign lo deriva del NÚMERO DE FACTURA (más confiable que su fecha) — se MANTIENE así,
 * NO usa derivarPeriodo como los 6 nuevos.
 */

// ─── Capa pura: texto → 5 campos ──────────────────────────────────────────────
// Exportada aparte (sin pdfjs) para poder testearla standalone con el texto ya extraído.
export const parsearOnSignDesdeTexto = (texto) => {
  const MESES_EN = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
  };

  // 1. Número de factura: 13 dígitos tras "Invoice #:". Ancla obligatoria — si no está, no es OnSign.
  const mNum = texto.match(/Invoice #:\s*(\d{13})/);
  if (!mNum) throw new Error("No se reconoce el formato de factura OnSign");
  const numeroFactura = mNum[1]; // "2026070184750"

  // 2-5. El número codifica el período: AAAA MM RRRRRRR (año, mes, recibo).
  // folio ← el recibo puro de 7 dígitos (el que va a numero_documento). periodo ← derivado del número
  // de factura (día 01 del mes facturado); se MANTIENE así (no derivarPeriodo).
  const folio = numeroFactura.slice(6); // "0184750" (recibo puro, sin año/mes)
  const periodo = `${numeroFactura.slice(0, 4)}-${numeroFactura.slice(4, 6)}-01`; // "2026-07-01"

  // 6. Monto total: "Total US$ 2,066.12". Ancla obligatoria — separador de miles ',' y decimal '.'.
  const mTot = texto.match(/Total\s+US\$\s*([\d,]+\.\d{2})/);
  if (!mTot) throw new Error("No se pudo leer el monto total de la factura OnSign");
  const monto = Number.parseFloat(mTot[1].replace(/,/g, "")); // 2066.12

  // 7. Cantidad de licencias: la línea "Subscription Renewal" cuyo período ARRANCA el día 01 (mes
  // completo). CRÍTICO anclar por "01 <Mes> <Año>", NO por magnitud ni posición: las líneas de
  // prorrateo arrancan día 13/21 con 1 licencia; la de mes completo arranca día 01 y trae el conteo
  // real (ej. 187). El nombre del plan entre el conteo y el "01" puede ser de UNA o VARIAS palabras
  // (OnSign lo cambió de "Professional (Custom)" a "Legacy" en 2026/04), así que se consume con
  // `\D+?` (no `\w+`, que solo cubre una palabra y fallaba con "Professional (Custom)"): tolera
  // espacios y paréntesis, no traga dígitos de otra línea y frena en el primer "01 <Mes>". Si no
  // matchea, NO se bloquea: se devuelve null y el usuario la completa a mano.
  const mLic = texto.match(/Subscription Renewal\s+(\d+)\s+\D+?\s+01\s+\w{3}\s+\d{4}/);
  const unidades = mLic ? Number.parseInt(mLic[1], 10) : null;

  // 8. Fecha del documento: "Date: 01 Jul 2026" → "2026-07-01". Best-effort (no bloquea): en OnSign
  // siempre coincide con el día 01 del período, pero se lee de su propia línea por robustez.
  let fecha = null;
  const mFecha = texto.match(/Date:\s*(\d{2})\s+([A-Za-z]{3})\s+(\d{4})/);
  if (mFecha) {
    const mm = MESES_EN[mFecha[2]];
    if (mm) fecha = `${mFecha[3]}-${String(mm).padStart(2, "0")}-${mFecha[1]}`;
  }

  // Shape transversal (idéntico a los 6 parsers nuevos). concepto=null: lo arma el hook.
  return {
    folio, // "0184750" (recibo puro) → numero_documento
    fecha, // "2026-07-01" o null
    periodo, // "2026-07-01" (derivado del número de factura, NO de la fecha)
    concepto: null, // OnSign no trae nombre de producto
    monto, // 2066.12
    moneda: "USD", // OnSign siempre factura en USD
    unidades, // 187 o null (licencias)
  };
};

// ─── Función principal: PDF → 5 campos ────────────────────────────────────────
export const parseOnSignPdf = async (file) => {
  const texto = await extraerTextoPdf(file);
  return parsearOnSignDesdeTexto(texto);
};
